import React, {
    useImperativeHandle,
    useRef,
    useEffect,
    forwardRef,
    useState,
} from "react";
import { Modal } from "bootstrap";
import { BACKOFFICE_BASE_URL } from "../../services/TapApiUrls";
import HttpAPICall from "../../services/HttpAPICall";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Loader from "../../components/ui/Loader/Loader";

const COUNT_ROW_DEFS = {
    group: [
        { id: "count_group_users", label: "User", moduleLabel: "Group", current: "total_users", max: "max_users" },
        { id: "count_group_sites", label: "Site", moduleLabel: "Group", current: "total_sites", max: "max_sites" },
    ],
    iam: [
        { id: "count_iam_assets", label: "Asset", moduleLabel: "Asset", current: "total_assets", max: "max_assets" },
    ],
    isc: [
        { id: "count_isc_warehouses", label: "Warehouse", moduleLabel: "Inventory", current: "total_warehouses", max: "max_warehouses" },
        { id: "count_isc_items", label: "Item", moduleLabel: "Inventory", current: "total_items", max: "max_items" },
    ],
    hrm: [
        { id: "count_hrm_employees", label: "Employee", moduleLabel: "HR", current: "total_employees", max: "max_employees" },
    ],
    scrm: [
        { id: "count_scrm_deals", label: "Deal", moduleLabel: "Sales", current: "total_deals", max: "max_deals" },
    ],
};

const COUNT_ROW_BY_ID = Object.values(COUNT_ROW_DEFS).flat().reduce((acc, row) => {
    acc[row.id] = row;
    return acc;
}, {});

const CountUpdate = forwardRef(({ onUpdateSuccess }, ref) => {
    const access_token = useSelector((state) => state.auth.access_token);
    const modalElem = useRef(null);
    const searchModal = useRef(null);
    const [formLoading, setFormLoading] = useState(false);

    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState({ tap_master_id: null });

    useImperativeHandle(ref, () => ({
        openEditModal: (payloadOrType, maybeData) => {
            if (Array.isArray(payloadOrType)) {
                const incoming = payloadOrType;
                if (incoming.length === 0) {
                    toast.error("Please select at least one count to update", {
                        position: toast.POSITION.TOP_RIGHT,
                    });
                    return;
                }

                const builtRows = incoming.map((item) => {
                    const def = COUNT_ROW_BY_ID[item.id] || {};
                    console.log(def);
                    
                    return {
                        id: item.id,
                        label: def.label || item.label || "-",
                        moduleLabel: def.moduleLabel || item.moduleLabel || "-",
                        current: def.current || item.current,
                        max: def.max || item.max,
                        currentValue: item.currentValue ?? "",
                        maxValue: item.maxValue ?? "",
                        newValue: "", 
                        module_id: item.module_id ?? null,
                    };
                });

                setRows(builtRows);
                setMeta({
                    tap_master_id: incoming[0]?.tap_master_id || null,
                });
                searchModal.current.show();
                return;
            }

            if (typeof payloadOrType === "string" && maybeData) {
                const type = payloadOrType;
                const data = maybeData;
                const map = {
                    group: ["count_group_users", "count_group_sites"],
                    iam: ["count_iam_assets"],
                    inventory: ["count_isc_warehouses", "count_isc_items"],
                    hrm: ["count_hrm_employees"],
                    crm_deal: ["count_scrm_deals"],
                };
                const ids = map[type] || [];
                const builtRows = ids.map((id) => {
                    const def = COUNT_ROW_BY_ID[id];
                    return {
                        id,
                        label: def.label,
                        moduleLabel: def.moduleLabel,
                        current: def.current,
                        max: def.max,
                        currentValue: data[def.current] ?? "",
                        maxValue: data[def.max] ?? "",
                        newValue: "",
                        module_id: data.module_id ?? null,
                    };
                });
                setRows(builtRows);
                setMeta({
                    tap_master_id: data.tap_master_id || null,
                });
                searchModal.current.show();
                return;
            }

            toast.error("Invalid data format", {
                position: toast.POSITION.TOP_RIGHT,
            });
        },

        closeModal: () => {
            searchModalClose();
        },
    }));

    useEffect(() => {
        modalElem.current = document.getElementById("editModalID");
        searchModal.current = new Modal(modalElem.current, {
            keyboard: false,
            backdrop: "static",
        });

        const handleHidden = () => {
            setFormLoading(false);
            setRows([]);
            setMeta({ tap_master_id: null });
        };

        modalElem.current.addEventListener("hidden.bs.modal", handleHidden);

        return () => {
            if (searchModal.current) searchModal.current.dispose();
            if (modalElem.current) {
                modalElem.current.removeEventListener("hidden.bs.modal", handleHidden);
            }
        };
    }, []);

    const searchModalClose = () => {
        if (searchModal.current) searchModal.current.hide();
        setFormLoading(false);
        setRows([]);
        setMeta({ tap_master_id: null });
    };

    /* ✅ New value change handler */
    const handleNewChange = (index, value) => {
        setRows((prev) => {
            const next = [...prev];
            next[index] = {
                ...next[index],
                newValue: value === "" ? "" : Number(value),
            };
            return next;
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (rows.length === 0) {
            toast.error("No counts selected", {
                position: toast.POSITION.TOP_RIGHT,
            });
            return;
        }

        // Validation — newValue negative check
        for (const row of rows) {
            if (row.newValue !== "" && Number(row.newValue) < 0) {
                toast.error("New value cannot be negative", {
                    position: toast.POSITION.TOP_RIGHT,
                });
                return;
            }
        }

        setFormLoading(true);

        /* ✅ Build payload:
           Group rows by module_id, then add total_* keys with newValue.
           
           Example output:
           {
             tap_master_id: "nexus",
             modules: [
               { module_id: 2, total_deals: 150 },
               { module_id: 4, total_assets: 1500 },
               { module_id: 5, total_employees: 150 },
               { module_id: 8, total_warehouses: 150, total_items: 15 }
             ]
           }
        */
        const grouped = {};
        rows.forEach((row) => {
            const key = row.module_id ?? "unknown";
            if (!grouped[key]) {
                grouped[key] = { module_id: row.module_id };
            }
            // ✅ Sirf tab bhejo jab user ne kuch fill kiya ho
            if (row.newValue !== "" && row.newValue !== null) {
                grouped[key][row.current] = Number(row.newValue) || 0;
            }
        });

        const payload = {
            tap_master_id: meta.tap_master_id,
            modules: Object.values(grouped),
        };

        // Optional: agar kisi module mein koi new value nahi bhari toh skip
        payload.modules = payload.modules.filter((m) => {
            const keys = Object.keys(m).filter((k) => k !== "module_id");
            return keys.length > 0;
        });

        if (payload.modules.length === 0) {
            toast.error("Please enter at least one new value", {
                position: toast.POSITION.TOP_RIGHT,
            });
            setFormLoading(false);
            return;
        }

        HttpAPICall.withAthorization(
            "PUT",
            BACKOFFICE_BASE_URL + "/group/update_count",
            access_token,
            {},
            payload,
            () => {
                toast.success("Counts updated successfully", {
                    position: toast.POSITION.TOP_RIGHT,
                });
                if (onUpdateSuccess) onUpdateSuccess();
                searchModalClose();
            },
            (error) => {
                toast.error(error?.message || "Failed to update counts", {
                    position: toast.POSITION.TOP_RIGHT,
                });
            }
        ).finally(() => setFormLoading(false));
    };

    const getModalTitle = () => {
        if (rows.length === 0) return "Update Counts";
        if (rows.length === 1) {
            return `Update ${rows[0].moduleLabel} - ${rows[0].label}`;
        }
        return `Update Counts (${rows.length})`;
    };

    return (
        <div className="modal fade" id="editModalID" tabIndex="-1" ref={modalElem}>
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{getModalTitle()}</h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={searchModalClose}
                            aria-label="Close"
                            disabled={formLoading}
                        />
                    </div>

                    <div className="modal-body">
                        {formLoading ? (
                            <div className="text-center py-5">
                                <Loader />
                            </div>
                        ) : (
                            <form id="countEditForm" onSubmit={handleSubmit}>
                                <div className="table-responsive">
                                    <table className="table table-bordered align-middle mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: "5%" }}>#</th>
                                                <th style={{ width: "25%" }}>Count</th>
                                                <th style={{ width: "35%" }}>Max</th>
                                                <th style={{ width: "35%" }}>New</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map((row, idx) => (
                                                <tr key={row.id}>
                                                    <td>{idx + 1}</td>
                                                    <td>{row.label}</td>

                                                    <td>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            value={row.maxValue ?? ""}
                                                            disabled
                                                            style={{ backgroundColor: "#e9ecef" }}
                                                            placeholder="Max"
                                                        />
                                                    </td>

                                                    {/* ✅ New — user fills here */}
                                                    <td>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            value={row.newValue}
                                                            onChange={(e) =>
                                                                handleNewChange(
                                                                    idx,
                                                                    e.target.value
                                                                )
                                                            }
                                                            min="0"
                                                            step="1"
                                                            placeholder="Enter New"
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </form>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={searchModalClose}
                            disabled={formLoading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            form="countEditForm"
                            className="btn btn-primary"
                            disabled={formLoading}
                        >
                            {formLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    Updating...
                                </>
                            ) : rows.length > 1 ? (
                                `Update All (${rows.length})`
                            ) : (
                                "Update Counts"
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default CountUpdate;